/**
 * The closed sets a road test's own record draws from: the standing distances a
 * point may be, and where the whole run's figures came from (SPEC-016).
 *
 * Its own module for the reason `carTakes.js` is: `carSchema.js` is the single
 * address for every closed set the content pack draws from, and it stays under
 * the repo's 350-line ceiling by re-exporting rather than by holding everything.
 * `carFigures.js` reads `QUARTER_MILE_MARK` from here too, so the mark has ONE
 * spelling that the sheet, the chart and the build validator all share — where
 * it used to have two, an ESM constant and a CommonJS copy of it, held together
 * by a test whose whole job was to notice when they drifted.
 *
 * ## Why a mark exists at all
 *
 * Every other point on a trace is a SPEED, and a speed is fully described by the
 * axis it is plotted against. A standing distance is not: it is a DISTANCE that
 * happens to have been recorded at a speed, and on the chart it is
 * indistinguishable from any other point. 11.8 seconds at 200 km/h is both the
 * last point of the F40's Road & Track run and its quarter-mile time, and
 * nothing about the pair says so.
 *
 * Which is also why the figure is not written down a second time under
 * `specs.quarterMile`: that would be two copies of one measurement, free to
 * disagree the first time either is corrected. The mark is addressing, not data.
 *
 * ## Why five, and why they are ordered
 *
 * These are the distances a drag strip and a European road test actually
 * publish, and no more. Ordered by DISTANCE ascending, which is the order they
 * happen in and therefore the order a reader meets them; a set sorted
 * alphabetically would put the kilometre first and the thousand feet second,
 * which is the shape of no run ever made.
 *
 * The vocabulary widened from a single value when the unicorn drop arrived. Most
 * cars will carry the quarter and nothing else — that is the one figure the spec
 * sheet prints, and the one the game sheets printed. The Lotec C1000 is what
 * made the set plural: it has the fullest published acceleration profile of any
 * car considered for this catalogue, and drawing four of its distances while
 * dropping the rest would have been a choice made by a schema rather than by a
 * source.
 *
 * AT MOST ONE OF EACH per trace, which `validateCar.js` enforces. The old rule
 * was "at most one mark in total", and this is strictly wider over the same
 * records: nothing that passed before fails now.
 */
export const TRACE_MARKS = [
    'eighth-mile',
    '1000-ft',
    'quarter-mile',
    'half-mile',
    'kilometre',
];

/**
 * The one mark that is not merely an annotation.
 *
 * The spec sheet prints THE quarter-mile row and reads it off the trace through
 * this constant (`specSheet.quarterMileRow`), so it is named rather than being
 * `TRACE_MARKS[2]` — a reordering of the list above must not be able to change
 * which figure a published sheet shows.
 */
export const QUARTER_MILE_MARK = 'quarter-mile';

/**
 * Where a trace's figures came from, and the field this content model had
 * needed since the first one was authored (SPEC-016 PR-16.2).
 *
 * `carSpecs.js` states a hard rule — a trace is ONE road test, ONE car, ONE day
 * — and eleven records answer `acceleration: null` rather than break it. That
 * rule is right and it is why this catalogue can be trusted. It was also, until
 * this vocabulary existed, a rule that said the rarest cars in the pack could
 * have no performance figure at all: a Cizeta and a Vector were never taken to
 * a strip by a magazine, and never will be.
 *
 * So the rule was not relaxed; it was made to SAY MORE. A simulation is still
 * one run of one model, which is the same shape and a different basis, and the
 * screen prints which. A simulated quarter mile is admitted AS a simulated
 * quarter mile rather than smuggled in beside a Car and Driver figure as though
 * the two were the same kind of fact.
 *
 * Three values, and `claimed` is deliberately not a synonym for `simulated`:
 *
 * - `tested` — a magazine took this car to a strip on a day. The fifteen traces
 *   authored before this field existed are all of these.
 * - `claimed` — the maker's own figures. Optimistic in a known direction.
 * - `simulated` — a third party's model. A function of the inputs somebody fed
 *   it, which is a completely different way of being wrong.
 *
 * Flattening the last two into one `estimated` would lose the only distinction
 * a reader could act on.
 *
 * REQUIRED wherever `acceleration` is not null. A trace with no basis defaults
 * to whatever the reader assumes, and the reader assumes `tested` — which is
 * precisely the failure this field exists to prevent.
 *
 * A property of the TRACE and never of a point: the one-test rule already says
 * every point came off the same car on the same day, so a per-point basis would
 * be a schema that permits the composite trace the rule forbids.
 */
export const TRACE_BASES = ['tested', 'claimed', 'simulated'];
